/**
 * Mock daemon used by demo mode (`demoMode: true`).
 *
 * It lets the whole PWA flow be exercised without a real PC daemon or Worker:
 * pairing, streaming chat with reasoning + stop, approval cards with four grant
 * scopes, Ed25519 signature verification, and session review data.
 * Everything is local and clearly labelled as mock in the UI.
 */
import type {
  ApprovalDecision,
  ChatDelta,
  ChatDone,
  ChatError,
  ModelSelection,
  SessionSource,
  ThinkingEffort,
  ThinkingMode,
} from '@luminis/protocol';
import { validateDecision } from '../approval';
import { newApprovalNonce, verifyApproval } from '../crypto/e2e';
import { randomId, sha256Hex, nowSeconds, clampText } from '../id';
import type {
  ApprovalView,
  ChatTurn,
  DeviceIdentityStored,
  SessionEventView,
  SessionView,
} from '../types';

export interface MockStreamHandlers {
  onDelta(delta: ChatDelta): void;
  onDone(done: ChatDone): void;
  onError(error: ChatError): void;
  onAbort?(): void;
}

export interface MockStreamHandle {
  abort(): void;
}

export interface MockStreamRequest {
  messageId: string;
  text: string;
  history: ChatTurn[];
  model: ModelSelection;
  thinking: ThinkingMode;
  effort: ThinkingEffort;
}

export interface MockDecideInput {
  decision: ApprovalDecision;
  args_hash: string;
  signature: string;
  nonce: string;
  confirm_high_risk: boolean;
}

const TOOL_SUMMARIES: Array<{
  tool: string;
  risk: ApprovalView['risk'];
  summary: string;
  plan?: string[];
}> = [
  {
    tool: 'write_file',
    risk: 'write',
    summary: '写入工作区文件：E:\\Luminis\\docs\\brief.md',
    plan: ['备份目标文件', '写入新内容', '校验哈希并写审计'],
  },
  {
    tool: 'run_cmd',
    risk: 'destructive',
    summary: '执行命令：Remove-Item -Recurse E:\\Luminis\\.tmp\\cache',
    plan: ['列出待删除目录', '二次确认', '删除并写审计'],
  },
  {
    tool: 'browser.open',
    risk: 'external',
    summary: '使用工作 profile 打开网络学堂并抓取公告',
  },
  {
    tool: 'read_file',
    risk: 'read',
    summary: '读取 DSH 会话摘要（只读）',
  },
];

export class MockDaemon {
  readonly identity: DeviceIdentityStored;
  readonly mode = 'mock' as const;
  private sessionState: SessionView[] | null = null;
  private sessionSyncedAt: number | null = null;

  constructor(identity: DeviceIdentityStored) {
    this.identity = identity;
  }

  /** Demo pairing accepts any well-formed 8-character code. */
  beginPairing(code: string): { ok: boolean; error?: string } {
    const compact = code.replace(/[\s-]/gu, '').toUpperCase();
    if (!/^[A-Z2-9]{8}$/u.test(compact)) {
      return { ok: false, error: '配对码格式不正确（示例：7K3F-9Q2M）' };
    }
    return { ok: true };
  }

  createApproval(overrides: Partial<ApprovalView> = {}): ApprovalView {
    const template =
      TOOL_SUMMARIES[Math.floor(Math.random() * TOOL_SUMMARIES.length)] ?? TOOL_SUMMARIES[0]!;
    const now = nowSeconds();
    return {
      approval_id: randomId('ap_'),
      tool: template.tool,
      risk: template.risk,
      summary: template.summary,
      args_preview: `tool=${template.tool}\nargs={"target":"E:\\\\Luminis","task_id":"task_demo"}\nrisk=${template.risk}`,
      args_hash: '',
      source: 'user',
      requested_at: now,
      expires_at: now + 24 * 60 * 60,
      plan: template.plan,
      status: 'pending',
      ...overrides,
    };
  }

  async seedApprovals(): Promise<ApprovalView[]> {
    const template = TOOL_SUMMARIES[1]!;
    const now = nowSeconds();
    const first: ApprovalView = {
      approval_id: randomId('ap_'),
      tool: 'run_cmd',
      risk: 'destructive',
      summary: '执行命令：Remove-Item -Recurse E:\\Luminis\\.tmp\\cache',
      args_preview: 'cmd=Remove-Item -Recurse .tmp/cache\ncwd=E:\\Luminis\nrisk=destructive',
      args_hash: '',
      source: 'external',
      requested_at: now - 60,
      expires_at: now + 24 * 60 * 60,
      plan: template.plan,
      status: 'pending',
    };
    const second: ApprovalView = {
      approval_id: randomId('ap_'),
      tool: 'write_file',
      risk: 'write',
      summary: '写入个人简报：%USERPROFILE%\\.luminis\\briefs\\2026-10-18.md',
      args_preview: 'path=%USERPROFILE%\\.luminis\\briefs\\2026-10-18.md\nbytes=2048',
      args_hash: '',
      source: 'system',
      requested_at: now - 300,
      expires_at: now + 24 * 60 * 60,
      plan: ['渲染简报 Markdown', '写入文件', '推送完成通知'],
      status: 'pending',
    };
    first.args_hash = await hashArgsPreview(first);
    second.args_hash = await hashArgsPreview(second);
    return [first, second];
  }

  async prepareApproval(approval: ApprovalView): Promise<ApprovalView> {
    return { ...approval, args_hash: await hashArgsPreview(approval) };
  }

  /**
   * Simulates the daemon side: validates the decision rules and verifies the
   * Ed25519 signature produced by the phone (in demo mode the "daemon" knows
   * the device public key because it runs in the same app).
   */
  async decide(
    approval: ApprovalView,
    input: MockDecideInput,
    now = nowSeconds(),
  ): Promise<{ ok: boolean; error?: string; updated: ApprovalView }> {
    const validation = validateDecision(approval, {
      decision: input.decision,
      args_hash: input.args_hash,
      confirm_high_risk: input.confirm_high_risk,
      now,
    });
    if (!validation.ok) return { ok: false, error: validation.message, updated: approval };
    const signatureOk = await verifyApproval(
      this.identity.signing_public_key,
      {
        approval_id: approval.approval_id,
        decision: input.decision,
        args_hash: input.args_hash,
        nonce: input.nonce,
      },
      input.signature,
    );
    if (!signatureOk) {
      return { ok: false, error: '审批签名校验失败（已完成占位验签）', updated: approval };
    }
    const granted = input.decision !== 'deny';
    return {
      ok: true,
      updated: {
        ...approval,
        status: granted ? 'granted' : 'denied',
        decision: input.decision,
        scope: input.decision,
        decided_at: now,
        signature: input.signature,
      },
    };
  }

  nonce(): string {
    return newApprovalNonce();
  }

  /** Streams a plausible assistant answer (reasoning + content) in chunks. */
  streamChat(request: MockStreamRequest, handlers: MockStreamHandlers): MockStreamHandle {
    let abortRequested = false;
    let seq = 0;
    const timers: Array<ReturnType<typeof setInterval>> = [];

    const reasoning = this.buildReasoning(request);
    const content = this.buildContent(request);
    const chunks: Array<{ kind: 'reasoning' | 'content'; text: string }> = [];
    const thinking = request.thinking !== 'disabled';

    if (thinking) {
      for (let index = 0; index < reasoning.length; index += 6) {
        chunks.push({ kind: 'reasoning', text: reasoning.slice(index, index + 6) });
      }
    }
    for (let index = 0; index < content.length; index += 8) {
      chunks.push({ kind: 'content', text: content.slice(index, index + 8) });
    }

    const start = setTimeout(() => {
      let index = 0;
      const interval = setInterval(() => {
        if (abortRequested) {
          clearInterval(interval);
          handlers.onAbort?.();
          return;
        }
        const chunk = chunks[index];
        if (!chunk) {
          clearInterval(interval);
          const inputTokens = Math.max(
            8,
            Math.round((request.text.length + request.history.length * 20) / 2),
          );
          const outputTokens = Math.max(12, Math.round((reasoning.length + content.length) / 2));
          handlers.onDone({
            message_id: request.messageId,
            model: request.model,
            usage: { input_tokens: inputTokens, output_tokens: outputTokens },
            finish_reason: 'stop',
          });
          return;
        }
        seq += 1;
        handlers.onDelta({
          message_id: request.messageId,
          seq,
          kind: chunk.kind,
          delta: chunk.text,
        });
        index += 1;
      }, 22);
      timers.push(interval);
    }, 350);
    timers.push(start as unknown as ReturnType<typeof setInterval>);

    return {
      abort: () => {
        abortRequested = true;
        for (const timer of timers) {
          clearTimeout(timer);
          clearInterval(timer);
        }
      },
    };
  }

  private buildReasoning(request: MockStreamRequest): string {
    const effort = request.effort === 'max' ? '深入' : request.effort === 'high' ? '完整' : '快速';
    return `用户意图：「${clampText(request.text, 40)}」。我按${effort}模式拆解：1) 识别这是手机端演示会话；2) 说明当前是 mock daemon，不连接真实 PC；3) 给出可执行的下一步建议，并提醒高危操作必须审批。`;
  }

  private buildContent(request: MockStreamRequest): string {
    const text = request.text;
    if (/审批|approval/iu.test(text)) {
      return '这里是演示审批流：审批卡会展示工具、风险等级、参数哈希和过期时间，并提供四档授权（一次性/本任务/限时 15 分钟/永久白名单）。高危操作需要二次确认，确认后手机用 Ed25519 对「approval_id + decision + args_hash + nonce」签名，PC 端验签通过才会执行。当前是 mock 数据，不会真的执行命令。';
    }
    if (/文件|目录|list/iu.test(text)) {
      return '演示回复：真实环境中，我会先调用只读工具列出目录（白名单自动执行），涉及写入或删除时会生成审批请求推送到手机。你可以在「审批」页看到演示卡片并测试四档授权。';
    }
    if (/微信|消息/iu.test(text)) {
      return '演示回复：微信只读摘要属于 M0.4，会在 PC 端用 UIA/OCR 读取未读消息并生成摘要，然后通过 Worker 推送到手机。全程不发消息、不改资料、不改变登录状态。';
    }
    if (/dsH|会话|session/iu.test(text)) {
      return '演示回复：会话审阅（DSH/Codex/OpenCode/VS Code）会在 M0.3 接入。你可以在「会话」页先查看 mock 的会话列表、token 与费用，以及会话事件。';
    }
    return `演示回复：我收到了「${clampText(text, 80)}」。这是 Luminis 的 mock 聊天：真实模式下会经 Cloudflare Worker 代理 DeepSeek，流式返回 reasoning_content 与 content，并支持停止生成、切换模型与思考强度。当前没有连接真实后端，所有内容均为本地模拟。`;
  }

  /**
   * Session-review snapshot. The first call seeds deterministic fixtures; later
   * calls advance the running session(s) by the elapsed wall-clock time so the
   * PWA's live refresh shows realistically moving progress/token/event values
   * without a real daemon. Pass `now` to make this deterministic in tests.
   */
  sessions(now = nowSeconds()): SessionView[] {
    this.syncSessions(now);
    return (this.sessionState ?? []).map(cloneSession);
  }

  sessionTail(source: SessionSource, sessionId: string, now = nowSeconds()): SessionEventView[] {
    const session = this.sessions(now).find(
      (item) => item.source === source && item.session_id === sessionId,
    );
    return session?.events ?? [];
  }

  private syncSessions(now: number): void {
    if (!this.sessionState) {
      this.sessionState = createMockSessions(now);
      this.sessionSyncedAt = now;
      return;
    }
    const previous = this.sessionSyncedAt ?? now;
    if (now <= previous) return;
    this.sessionState = advanceMockSessions(this.sessionState, now - previous, now);
    this.sessionSyncedAt = now;
  }
}

/** Token burn rate of the running mock session (tokens / second). */
const MOCK_ACTIVE_TOKENS_PER_SECOND = 1.2;
/** CNY per token, roughly DeepSeek Flash pricing (~¥12 / 1M tokens). */
const MOCK_ACTIVE_COST_PER_TOKEN = 0.000_012;
/** Several seconds pass between appended progress events on the live tick. */
const MOCK_EVENT_INTERVAL_SECONDS = 3;
const MOCK_MAX_EVENTS = 80;

function cloneSession(session: SessionView): SessionView {
  return { ...session, events: session.events.map((event) => ({ ...event })) };
}

function phaseForProgress(progress: number): string {
  if (progress < 25) return '读取上下文';
  if (progress < 50) return '实现中';
  if (progress < 75) return '测试中';
  if (progress < 92) return '整理报告';
  return '等待下一条指令';
}

function cloneEvents(events: SessionEventView[]): SessionEventView[] {
  return events.map((event) => ({ ...event }));
}

/** Deterministic session fixtures for demo mode / tests (relative to `now`). */
export function createMockSessions(now = nowSeconds()): SessionView[] {
  return [
    {
      source: 'dsh',
      session_id: 'dsh_demo_001',
      title: '修复 Worker 断线重连补发',
      status: 'active',
      started_at: now - 3600,
      last_event_at: now - 45,
      tokens: 18_420,
      cost_cny: 0.21,
      progress: 62,
      phase: '测试中',
      model: 'deepseek-flash',
      events: [
        { ts: now - 900, kind: 'message', role: 'user', text: '检查离线队列的序号是否单调递增' },
        {
          ts: now - 700,
          kind: 'tool',
          tool: 'read_file',
          text: 'apps/worker/src/relay/queue.ts',
        },
        {
          ts: now - 300,
          kind: 'tool',
          tool: 'run_cmd',
          text: 'pnpm --filter @luminis/worker test',
        },
        { ts: now - 90, kind: 'progress', text: '进度 58% · 测试中' },
        { ts: now - 45, kind: 'status', text: '测试通过，正在整理报告' },
      ],
    },
    {
      source: 'codex',
      session_id: 'codex_demo_002',
      title: 'PWA 审批卡交互实现',
      status: 'idle',
      started_at: now - 7200,
      last_event_at: now - 1800,
      tokens: 9_120,
      cost_cny: 0.12,
      progress: 45,
      phase: '等待手机端联调',
      model: 'gpt-5.1-codex',
      events: [
        { ts: now - 4000, kind: 'message', role: 'user', text: '实现四档授权按钮和二次确认' },
        { ts: now - 3600, kind: 'file', text: 'src/components/ApprovalCard.tsx' },
        { ts: now - 1800, kind: 'status', text: '等待手机端联调' },
      ],
    },
    {
      source: 'opencode',
      session_id: 'oc_demo_003',
      title: '微信只读探针 UIA 调研',
      status: 'done',
      started_at: now - 90_000,
      last_event_at: now - 86_400,
      tokens: 25_000,
      cost_cny: 0.31,
      progress: 100,
      phase: '已完成',
      model: 'deepseek-v4-pro',
      events: [
        { ts: now - 90_000, kind: 'message', role: 'user', text: '调研微信 4.1 的 UIA 可读性' },
        { ts: now - 88_000, kind: 'tool', tool: 'run_cmd', text: 'probes/wechat-uia.ps1' },
        { ts: now - 86_400, kind: 'status', text: '完成，结论已写入 docs' },
      ],
    },
    {
      source: 'vscode',
      session_id: 'vscode_demo_004',
      title: 'VS Code 扩展事件采集',
      status: 'idle',
      started_at: now - 8000,
      last_event_at: now - 7200,
      tokens: 3_200,
      cost_cny: 0.04,
      progress: 10,
      phase: '等待扩展安装',
      events: [
        { ts: now - 7600, kind: 'file', text: 'extensions/luminis-collector/src/extension.ts' },
        { ts: now - 7200, kind: 'status', text: '等待扩展安装（M0.3）' },
      ],
    },
    {
      // Older than the default 7-day window: must stay hidden from the list but
      // remain available if the daemon ever supports an explicit wider range.
      source: 'luminis',
      session_id: 'luminis_demo_005',
      title: '周报整理（更早会话）',
      status: 'done',
      started_at: now - 12 * 86_400 - 1800,
      last_event_at: now - 12 * 86_400,
      tokens: 5_000,
      cost_cny: 0.06,
      progress: 100,
      phase: '已完成',
      events: [
        { ts: now - 12 * 86_400 - 1800, kind: 'message', role: 'user', text: '整理这周的周报' },
        { ts: now - 12 * 86_400, kind: 'status', text: '已完成' },
      ],
    },
  ];
}

/**
 * Advance the running mock session(s) by `elapsedSeconds` and return a new
 * array. Only `active` sessions evolve; idle/done/error sessions are untouched.
 * Pure and deterministic (`now` is injected) so live-refresh behaviour is
 * unit-testable without timers.
 */
export function advanceMockSessions(
  sessions: readonly SessionView[],
  elapsedSeconds: number,
  now = nowSeconds(),
): SessionView[] {
  if (!(elapsedSeconds > 0)) return sessions as SessionView[];
  return sessions.map((session) => {
    if (session.status !== 'active') return session;
    const gainedTokens = Math.max(1, Math.round(elapsedSeconds * MOCK_ACTIVE_TOKENS_PER_SECOND));
    const tokens = (session.tokens ?? 0) + gainedTokens;
    const cost = Number(
      ((session.cost_cny ?? 0) + gainedTokens * MOCK_ACTIVE_COST_PER_TOKEN).toFixed(6),
    );
    const progress = Math.min(99, (session.progress ?? 0) + elapsedSeconds * 0.4);
    const phase = phaseForProgress(progress);
    let events = cloneEvents(session.events);
    // Cadence is based on the last event timestamp, so appending still works when
    // the UI polls faster than the event interval (2.5s poll vs 3s cadence).
    const lastEvent = session.events[session.events.length - 1];
    const sinceLastEvent = lastEvent ? now - lastEvent.ts : Number.POSITIVE_INFINITY;
    if (sinceLastEvent >= MOCK_EVENT_INTERVAL_SECONDS) {
      const progressEvent: SessionEventView = {
        ts: Math.max(now, session.last_event_at),
        kind: 'progress',
        text: `进度 ${Math.round(progress)}% · ${phase}`,
      };
      events = [...events, progressEvent].slice(-MOCK_MAX_EVENTS);
    }
    return {
      ...session,
      tokens,
      cost_cny: cost,
      progress,
      phase,
      last_event_at: Math.max(now, session.last_event_at),
      events,
    };
  });
}

export async function hashArgsPreview(approval: ApprovalView): Promise<string> {
  return sha256Hex(
    `${approval.tool}|${approval.args_preview ?? approval.summary}|${approval.task_id ?? ''}`,
  );
}
